import React, { useState } from "react";
import SharedHelmet from "../../Helmet";
import { SecondAds } from "../../../Ads";
const TITLE = "Kalkulator Dosis Harian Vitamin - Hitung Kebutuhan Vitamin Anda";
const DESCRIPTION =
  "Kalkulator dosis harian vitamin yang akurat untuk menghitung kebutuhan harian Anda akan vitamin esensial berdasarkan usia dan jenis kelamin. Meliputi vitamin A, C, D, E, K dan semua vitamin B kompleks dengan penjelasan rinci tentang manfaat setiap vitamin dan sumber alaminya.";
const KEYWORDS =
  "kalkulator vitamin, dosis harian vitamin, kebutuhan tubuh akan vitamin, vitamin A, vitamin C, vitamin D, vitamin E, vitamin K, vitamin B kompleks, tiamin B1, riboflavin B2, niasin B3, asam folat, vitamin B12, sumber vitamin, kekurangan vitamin";
const OG_URL = "https://do-calculate.com/calculator/id/vitamin_daily_dose/";
const vitaminsList = {
  A: "Vitamin A",
  C: "Vitamin C",
  D: "Vitamin D",
  E: "Vitamin E",
  K: "Vitamin K",
  B1: "Tiamin (B1)",
  B2: "Riboflavin (B2)",
  B3: "Niasin (B3)",
  B6: "Piridoksin (B6)",
  B9: "Asam Folat (B9)",
  B12: "Kobalamin (B12)",
  B5: "Asam Pantotenat (B5)",
  B7: "Biotin (B7)",
  Choline: "Kolin",
};
const vitaminUnits = {
  A: "mikrogram",
  C: "mg",
  D: "mikrogram",
  E: "mg",
  K: "mikrogram",
  B1: "mg",
  B2: "mg",
  B3: "mg",
  B6: "mg",
  B9: "mikrogram",
  B12: "mikrogram",
  B5: "mg",
  B7: "mikrogram",
  Choline: "mg",
};
const VitaminDailyDoseCalculator = () => {
  const [selectedVitamin, setSelectedVitamin] = useState("A");
  const [age, setAge] = useState("");
  const [sex, setSex] = useState("male");
  const [isPregnant, setIsPregnant] = useState(false);
  const [isLactating, setIsLactating] = useState(false);
  const [dose, setDose] = useState(null);
  const [error, setError] = useState("");
  // Vitamin dosage data based on age and sex (in micrograms or mg)
  const getVitaminDose = (vitamin, ageValue, gender, pregnant, lactating) => {
    const dosageTable = {
      A: {
        // Retinol Activity Equivalents (RAE) in micrograms
        male: {
          "0-6m": 400,
          "7-12m": 500,
          "1-3y": 300,
          "4-8y": 400,
          "9-13y": 600,
          "14-18y": 900,
          "19-50y": 900,
          "51+y": 900,
        },
        female: {
          "0-6m": 400,
          "7-12m": 500,
          "1-3y": 300,
          "4-8y": 400,
          "9-13y": 600,
          "14-18y": 700,
          "19-50y": 700,
          "51+y": 700,
        },
        pregnant: 770,
        lactating: 1300,
      },
      C: {
        // Vitamin C in mg
        male: {
          "0-6m": 40,
          "7-12m": 50,
          "1-3y": 15,
          "4-8y": 25,
          "9-13y": 45,
          "14-18y": 75,
          "19-50y": 90,
          "51+y": 90,
        },
        female: {
          "0-6m": 40,
          "7-12m": 50,
          "1-3y": 15,
          "4-8y": 25,
          "9-13y": 45,
          "14-18y": 65,
          "19-50y": 75,
          "51+y": 75,
        },
        pregnant: 85,
        lactating: 120,
      },
      D: {
        // Vitamin D in micrograms
        male: {
          "0-6m": 10,
          "7-12m": 10,
          "1-3y": 15,
          "4-8y": 15,
          "9-13y": 15,
          "14-18y": 15,
          "19-50y": 15,
          "51-70y": 15,
          "71+y": 20,
        },
        female: {
          "0-6m": 10,
          "7-12m": 10,
          "1-3y": 15,
          "4-8y": 15,
          "9-13y": 15,
          "14-18y": 15,
          "19-50y": 15,
          "51-70y": 15,
          "71+y": 20,
        },
        pregnant: 15,
        lactating: 15,
      },
      E: {
        // Vitamin E in mg
        male: {
          "0-6m": 4,
          "7-12m": 5,
          "1-3y": 6,
          "4-8y": 7,
          "9-13y": 11,
          "14-18y": 15,
          "19-50y": 15,
          "51+y": 15,
        },
        female: {
          "0-6m": 4,
          "7-12m": 5,
          "1-3y": 6,
          "4-8y": 7,
          "9-13y": 11,
          "14-18y": 15,
          "19-50y": 15,
          "51+y": 15,
        },
        pregnant: 15,
        lactating: 19,
      },
      K: {
        // Vitamin K in micrograms
        male: {
          "0-6m": 2,
          "7-12m": 2.5,
          "1-3y": 30,
          "4-8y": 55,
          "9-13y": 60,
          "14-18y": 75,
          "19-50y": 120,
          "51+y": 120,
        },
        female: {
          "0-6m": 2,
          "7-12m": 2.5,
          "1-3y": 30,
          "4-8y": 55,
          "9-13y": 60,
          "14-18y": 75,
          "19-50y": 90,
          "51+y": 90,
        },
        pregnant: 90,
        lactating: 90,
      },
      B1: {
        // Thiamin in mg
        male: {
          "0-6m": 0.2,
          "7-12m": 0.3,
          "1-3y": 0.5,
          "4-8y": 0.6,
          "9-13y": 0.9,
          "14-18y": 1.2,
          "19-50y": 1.2,
          "51+y": 1.2,
        },
        female: {
          "0-6m": 0.2,
          "7-12m": 0.3,
          "1-3y": 0.5,
          "4-8y": 0.6,
          "9-13y": 0.9,
          "14-18y": 1.0,
          "19-50y": 1.1,
          "51+y": 1.1,
        },
        pregnant: 1.4,
        lactating: 1.4,
      },
      B2: {
        // Riboflavin in mg
        male: {
          "0-6m": 0.3,
          "7-12m": 0.4,
          "1-3y": 0.5,
          "4-8y": 0.6,
          "9-13y": 0.9,
          "14-18y": 1.3,
          "19-50y": 1.3,
          "51+y": 1.3,
        },
        female: {
          "0-6m": 0.3,
          "7-12m": 0.4,
          "1-3y": 0.5,
          "4-8y": 0.6,
          "9-13y": 0.9,
          "14-18y": 1.0,
          "19-50y": 1.1,
          "51+y": 1.1,
        },
        pregnant: 1.4,
        lactating: 1.6,
      },
      B3: {
        // Niacin in mg
        male: {
          "0-6m": 2,
          "7-12m": 4,
          "1-3y": 6,
          "4-8y": 8,
          "9-13y": 12,
          "14-18y": 16,
          "19-50y": 16,
          "51+y": 16,
        },
        female: {
          "0-6m": 2,
          "7-12m": 4,
          "1-3y": 6,
          "4-8y": 8,
          "9-13y": 12,
          "14-18y": 14,
          "19-50y": 14,
          "51+y": 14,
        },
        pregnant: 18,
        lactating: 17,
      },
      B6: {
        // Pyridoxine in mg
        male: {
          "0-6m": 0.1,
          "7-12m": 0.3,
          "1-3y": 0.5,
          "4-8y": 0.6,
          "9-13y": 1.0,
          "14-18y": 1.3,
          "19-50y": 1.3,
          "51-70y": 1.7,
          "71+y": 1.7,
        },
        female: {
          "0-6m": 0.1,
          "7-12m": 0.3,
          "1-3y": 0.5,
          "4-8y": 0.6,
          "9-13y": 1.0,
          "14-18y": 1.2,
          "19-50y": 1.3,
          "51-70y": 1.5,
          "71+y": 1.5,
        },
        pregnant: 1.9,
        lactating: 2.0,
      },
      B9: {
        // Folate in micrograms
        male: {
          "0-6m": 65,
          "7-12m": 80,
          "1-3y": 150,
          "4-8y": 200,
          "9-13y": 300,
          "14-18y": 400,
          "19-50y": 400,
          "51+y": 400,
        },
        female: {
          "0-6m": 65,
          "7-12m": 80,
          "1-3y": 150,
          "4-8y": 200,
          "9-13y": 300,
          "14-18y": 400,
          "19-50y": 400,
          "51+y": 400,
        },
        pregnant: 600,
        lactating: 500,
      },
      B12: {
        // Cobalamin in micrograms
        male: {
          "0-6m": 0.4,
          "7-12m": 0.5,
          "1-3y": 0.9,
          "4-8y": 1.2,
          "9-13y": 1.8,
          "14-18y": 2.4,
          "19-50y": 2.4,
          "51+y": 2.4,
        },
        female: {
          "0-6m": 0.4,
          "7-12m": 0.5,
          "1-3y": 0.9,
          "4-8y": 1.2,
          "9-13y": 1.8,
          "14-18y": 2.4,
          "19-50y": 2.4,
          "51+y": 2.4,
        },
        pregnant: 2.6,
        lactating: 2.8,
      },
      B5: {
        // Pantothenic Acid in mg
        male: {
          "0-6m": 1.7,
          "7-12m": 1.8,
          "1-3y": 2,
          "4-8y": 3,
          "9-13y": 4,
          "14-18y": 5,
          "19-50y": 5,
          "51+y": 5,
        },
        female: {
          "0-6m": 1.7,
          "7-12m": 1.8,
          "1-3y": 2,
          "4-8y": 3,
          "9-13y": 4,
          "14-18y": 5,
          "19-50y": 5,
          "51+y": 5,
        },
        pregnant: 6,
        lactating: 7,
      },
      B7: {
        // Biotin in micrograms
        male: {
          "0-6m": 5,
          "7-12m": 6,
          "1-3y": 8,
          "4-8y": 12,
          "9-13y": 20,
          "14-18y": 25,
          "19-50y": 30,
          "51+y": 30,
        },
        female: {
          "0-6m": 5,
          "7-12m": 6,
          "1-3y": 8,
          "4-8y": 12,
          "9-13y": 20,
          "14-18y": 25,
          "19-50y": 30,
          "51+y": 30,
        },
        pregnant: 30,
        lactating: 35,
      },
      Choline: {
        // Choline in mg
        male: {
          "0-6m": 125,
          "7-12m": 150,
          "1-3y": 200,
          "4-8y": 250,
          "9-13y": 375,
          "14-18y": 550,
          "19-50y": 550,
          "51+y": 550,
        },
        female: {
          "0-6m": 125,
          "7-12m": 150,
          "1-3y": 200,
          "4-8y": 250,
          "9-13y": 375,
          "14-18y": 400,
          "19-50y": 425,
          "51+y": 425,
        },
        pregnant: 450,
        lactating: 550,
      },
    };
    // Determine age category
    let ageCategory;
    const ageNum = parseInt(ageValue);
    if (ageNum < 1) {
      if (ageNum < 0.5) ageCategory = "0-6m";
      else ageCategory = "7-12m";
    } else if (ageNum >= 1 && ageNum <= 3) {
      ageCategory = "1-3y";
    } else if (ageNum >= 4 && ageNum <= 8) {
      ageCategory = "4-8y";
    } else if (ageNum >= 9 && ageNum <= 13) {
      ageCategory = "9-13y";
    } else if (ageNum >= 14 && ageNum <= 18) {
      ageCategory = "14-18y";
    } else if (ageNum >= 19 && ageNum <= 50) {
      ageCategory = "19-50y";
    } else if (ageNum >= 51 && ageNum <= 70) {
      ageCategory = "51-70y" in dosageTable[vitamin][gender]
        ? "51-70y"
        : "51+y";
    } else {
      ageCategory = "71+y" in dosageTable[vitamin][gender]
        ? "71+y"
        : "51+y";
    }
    // Check for pregnancy or lactation
    if (gender === "female" && pregnant) {
      return dosageTable[vitamin].pregnant;
    }
    if (gender === "female" && lactating) {
      return dosageTable[vitamin].lactating;
    }
    return dosageTable[vitamin][gender][ageCategory];
  };
  const calculateDailyDose = () => {
    try {
      if (!age || age < 0 || age > 120) {
        setError("Harap masukkan usia yang valid (0-120 tahun)");
        setDose(null);
        return;
      }
      const calculatedDose = getVitaminDose(
        selectedVitamin,
        age,
        sex,
        isPregnant,
        isLactating
      );
      setDose(calculatedDose);
      setError("");
    } catch (error) {
      setDose(null);
      setError("Terjadi kesalahan dalam menghitung dosis. Harap periksa data yang dimasukkan.");
    }
  };
  const handleCalculate = (e) => {
    e.preventDefault();
    calculateDailyDose();
  };
  const handleReset = () => {
    setSelectedVitamin("A");
    setAge("");
    setSex("male");
    setIsPregnant(false);
    setIsLactating(false);
    setDose(null);
    setError("");
  };
  // Generate Schema.org structured data
  const generateSchemaData = () => {
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Halaman Utama",
          item: "https://do-calculate.com/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Kalkulator",
          item: "https://do-calculate.com/id/all-calculators/",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: "Kalkulator Vitamin",
          item: OG_URL,
        },
      ],
    };
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "Bagaimana cara menghitung kebutuhan vitamin harian saya?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Untuk menghitung kebutuhan vitamin harian Anda, masukkan usia Anda, pilih jenis kelamin dan vitamin yang diinginkan di kalkulator vitamin. Kebutuhan bervariasi tergantung usia, jenis kelamin, dan kondisi kesehatan.",
          },
        },
        {
          "@type": "Question",
          name: "Apa saja vitamin terpenting bagi tubuh?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Vitamin terpenting meliputi: Vitamin D untuk tulang, Vitamin C untuk kekebalan tubuh, Vitamin A untuk penglihatan, Vitamin B12 untuk saraf, dan Asam Folat terutama untuk ibu hamil.",
          },
        },
        {
          "@type": "Question",
          name: "Apa saja gejala kekurangan vitamin?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Gejala kekurangan vitamin meliputi: kelelahan dan kelemahan, sistem kekebalan lemah, masalah kulit, tulang lemah, anemia, dan masalah saraf tergantung jenis vitamin yang kurang.",
          },
        },
      ],
    };
    const calculatorSchema = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "Kalkulator Dosis Harian Vitamin",
      description: DESCRIPTION,
      url: OG_URL,
      applicationCategory: "HealthApplication",
      operatingSystem: "Any",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
      },
    };
    return [breadcrumbSchema, faqSchema, calculatorSchema];
  };
  return (
    <div className="ltr" >
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={generateSchemaData()}
      />
      <div>
        <br />
        <div
          className="bg-success text-white text-center"
          style={{ padding: "15px", borderRadius: "12px" }}
        >
          <h1 style={{ fontSize: "22px", margin: 0 }}>{TITLE}</h1>
        </div>
        <div className="border border-light mt-3 p-3 bg-white shadow-sm">
          <div className="mb-4">
            <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
              <strong>Kalkulator Dosis Harian Vitamin</strong> membantu Anda mengetahui kebutuhan vitamin harian Anda berdasarkan usia, jenis kelamin, dan kondisi kesehatan Anda. Kalkulator ini mencakup semua vitamin penting termasuk <strong>A, C, D, E, K</strong> dan semua vitamin <strong>B kompleks</strong> dengan informasi rinci tentang manfaat dan sumber setiap vitamin.
            </p>
          </div>
          <hr />
          <div className="row">
           <div className="col-sm-12 col-md-6 calculatorStyle">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>
                  Hitung Kebutuhan Harian Anda
                </h2>
              </div>
              <form onSubmit={handleCalculate}>
                <div className="form-group mb-3">
                  <label  htmlFor="vitamin">
                    <strong>Pilih Vitamin:</strong>
                  </label>
                  <select
                    className="form-control"
                    id="vitamin"
                    value={selectedVitamin}
                    onChange={(e) => setSelectedVitamin(e.target.value)}
                  >
                    {Object.keys(vitaminsList).map((key) => (
                      <option key={key} value={key}>
                        {vitaminsList[key]}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group mb-3">
                  <label  htmlFor="age">
                    <strong>Usia (tahun):</strong>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    id="age"
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                    placeholder="Masukkan usia Anda"
                    min="0"
                    max="120"
                    required
                  />
                </div>
                <div className="form-group mb-3">
                  <label  htmlFor="sex">
                    <strong>Jenis Kelamin:</strong>
                  </label>
                  <select
                    className="form-control"
                    id="sex"
                    value={sex}
                    onChange={(e) => {
                      setSex(e.target.value);
                      if (e.target.value === "male") {
                        setIsPregnant(false);
                        setIsLactating(false);
                      }
                    }}
                  >
                    <option value="male">Laki-laki</option>
                    <option value="female">Perempuan</option>
                  </select>
                </div>
                {sex === "female" && (
                  <>
                    <div className="form-group mb-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="pregnant"
                          checked={isPregnant}
                          onChange={(e) => {
                            setIsPregnant(e.target.checked);
                            if (e.target.checked) setIsLactating(false);
                          }}
                        />
                        <label className="form-check-label" htmlFor="pregnant">
                          Hamil
                        </label>
                      </div>
                    </div>
                    <div className="form-group mb-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="lactating"
                          checked={isLactating}
                          onChange={(e) => {
                            setIsLactating(e.target.checked);
                            if (e.target.checked) setIsPregnant(false);
                          }}
                        />
                        <label className="form-check-label" htmlFor="lactating">
                          Menyusui
                        </label>
                      </div>
                    </div>
                  </>
                )}
                <hr />
                <div className="d-flex gap-2">
                  <button
                    type="submit"
                    className="btn bg-success text-white"
                    style={{ fontSize: "16px", padding: "10px 30px" }}
                  >
                    Hitung
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleReset}
                    style={{ fontSize: "16px", padding: "10px 30px" }}
                  >
                    Hapus
                  </button>
                </div>
              </form>
            </div>
            <div className="col-sm-12 col-md-4">
              {error && (
                <div className="alert alert-warning" role="alert">
                  <strong>Kesalahan:</strong> {error}
                </div>
              )}
              {dose !== null && !error && (
                <div className="calculatorStyle">
                  <div className="p-3 mb-3 bg-success text-white text-center rounded">
                    <h3 style={{ fontSize: "18px", margin: 0 }}>Hasil</h3>
                  </div>
                  <div
                    className="alert alert-success"
                    style={{ fontSize: "16px", lineHeight: "1.8" }}
                  >
                    <h4 style={{ fontSize: "17px", marginBottom: "15px" }}>
                      Dosis harian yang direkomendasikan:
                    </h4>
                    <div
                      style={{
                        textAlign: "center",
                        padding: "20px",
                        backgroundColor: "#d4edda",
                        borderRadius: "8px",
                        marginBottom: "15px",
                      }}
                    >
                      <div style={{ fontSize: "18px", marginBottom: "10px" }}>
                        <strong>{vitaminsList[selectedVitamin]}</strong>
                      </div>
                      <div
                        style={{
                          fontSize: "32px",
                          fontWeight: "bold",
                          color: "#155724",
                        }}
                      >
                        {dose} {vitaminUnits[selectedVitamin]}
                      </div>
                    </div>
                    <div style={{ fontSize: "14px", marginTop: "15px" }}>
                      <strong>Catatan:</strong>
                      <ul style={{ marginTop: "10px", lineHeight: "1.6" }}>
                        <li>
                          Dosis ini mewakili Angka Kecukupan Gizi (RDA) yang direkomendasikan
                        </li>
                        <li>
                          Kebutuhan aktual Anda mungkin berbeda tergantung kondisi kesehatan dan pola makan Anda
                        </li>
                        <li>Konsultasikan dengan dokter Anda sebelum mengonsumsi suplemen apa pun</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              <SecondAds />
            </div>
          </div>
          <hr />
          <div
            className="alert alert-info"
            style={{ fontSize: "15px", lineHeight: "1.8" }}
          >
            <strong>💡 Informasi Penting:</strong> Hasil berdasarkan rekomendasi Dietary Reference Intakes (DRI) dari National Institutes of Health AS. Anda mungkin memerlukan dosis lebih tinggi dalam kondisi tertentu berdasarkan konsultasi dokter.
          </div>
        </div>
        <br />
        {/* Vitamins Table */}
        <div className="border border-light p-3 bg-white shadow-sm">
          <h2 style={{ fontSize: "20px", marginBottom: "15px" }}>
            Hitung Dosis Harian Setiap Vitamin
          </h2>
          <table className="table table-bordered table-hover">
            <thead>
              <tr style={{ backgroundColor: "#e7f3fe" }}>
                <th style={{ textAlign: "center", padding: "12px" }}>
                  Nama Vitamin
                </th>
                <th style={{ textAlign: "center", padding: "12px" }}>
                  Hitung Dosis Harian
                </th>
               </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: "10px" }}>Vitamin A</td>
                <td style={{ padding: "10px" }}>
                  <a href="/calculator/id/vitamin_daily_dose/a/">
                    Hitung dosis harian Vitamin A
                  </a>
                </td>
              </tr>
              <tr>
                <td style={{ padding: "10px" }}>Vitamin C</td>
                <td style={{ padding: "10px" }}>
                  <a href="/calculator/id/vitamin_daily_dose/c/">
                    Hitung dosis harian Vitamin C
                  </a>
                </td>
              </tr>
              <tr>
                <td style={{ padding: "10px" }}>Vitamin D</td>
                <td style={{ padding: "10px" }}>
                  <a href="/calculator/id/vitamin_daily_dose/d/">
                    Hitung dosis harian Vitamin D
                  </a>
                </td>
              </tr>
              <tr>
                <td style={{ padding: "10px" }}>Vitamin E</td>
                <td style={{ padding: "10px" }}>
                  <a href="/calculator/id/vitamin_daily_dose/e/">
                    Hitung dosis harian Vitamin E
                  </a>
                </td>
              </tr>
              <tr>
                <td style={{ padding: "10px" }}>Vitamin K</td>
                <td style={{ padding: "10px" }}>
                  <a href="/calculator/id/vitamin_daily_dose/k/">
                    Hitung dosis harian Vitamin K
                  </a>
                </td>
              </tr>
              <tr>
                <td style={{ padding: "10px" }}>Tiamin (B1)</td>
                <td style={{ padding: "10px" }}>
                  <a href="/calculator/id/vitamin_daily_dose/b1/">
                    Hitung dosis harian Vitamin Tiamin (B1)
                  </a>
                </td>
              </tr>
              <tr>
                <td style={{ padding: "10px" }}>Riboflavin (B2)</td>
                <td style={{ padding: "10px" }}>
                  <a href="/calculator/id/vitamin_daily_dose/b2/">
                    Hitung dosis harian Vitamin Riboflavin (B2)
                  </a>
                </td>
              </tr>
              <tr>
                <td style={{ padding: "10px" }}>Niasin (B3)</td>
                <td style={{ padding: "10px" }}>
                  <a href="/calculator/id/vitamin_daily_dose/b3/">
                    Hitung dosis harian Vitamin Niasin (B3)
                  </a>
                </td>
              </tr>
              <tr>
                <td style={{ padding: "10px" }}>Asam Pantotenat (B5)</td>
                <td style={{ padding: "10px" }}>
                  <a href="/calculator/id/vitamin_daily_dose/b5/">
                    Hitung dosis harian Vitamin Asam Pantotenat (B5)
                  </a>
                </td>
              </tr>
              <tr>
                <td style={{ padding: "10px" }}>Piridoksin (B6)</td>
                <td style={{ padding: "10px" }}>
                  <a href="/calculator/id/vitamin_daily_dose/b6/">
                    Hitung dosis harian Vitamin Piridoksin (B6)
                  </a>
                </td>
              </tr>
              <tr>
                <td style={{ padding: "10px" }}>Biotin (B7)</td>
                <td style={{ padding: "10px" }}>
                  <a href="/calculator/id/vitamin_daily_dose/b7/">
                    Hitung dosis harian Vitamin Biotin (B7)
                  </a>
                </td>
              </tr>
              <tr>
                <td style={{ padding: "10px" }}>Asam Folat (B9)</td>
                <td style={{ padding: "10px" }}>
                  <a href="/calculator/id/vitamin_daily_dose/b9/">
                    Hitung dosis harian Vitamin Asam Folat (B9)
                  </a>
                </td>
              </tr>
              <tr>
                <td style={{ padding: "10px" }}>Kobalamin (B12)</td>
                <td style={{ padding: "10px" }}>
                  <a href="/calculator/id/vitamin_daily_dose/b12/">
                    Hitung dosis harian Vitamin Kobalamin (B12)
                  </a>
                </td>
              </tr>
              <tr>
                <td style={{ padding: "10px" }}>Kolin</td>
                <td style={{ padding: "10px" }}>
                  <a href="/calculator/id/vitamin_daily_dose/choline/">
                    Hitung dosis harian Kolin
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <br />
        {/* Educational Content */}
        <div className="row">
          <div className="col-12 col-md-8">
            <div className="info-sections-container">
              <div className="info-section">
                <div className="info-section-header">
                  <strong>Panduan Lengkap Vitamin Esensial</strong>
                </div>
                <div className="info-section-body">
                  <p style={{ lineHeight: "1.8" }}>
                    <strong>Vitamin</strong> adalah senyawa organik esensial yang dibutuhkan tubuh dalam jumlah kecil untuk pertumbuhan yang tepat dan pemeliharaan kesehatan. Sebagian besar vitamin tidak dapat diproduksi oleh tubuh, sehingga harus diperoleh dari makanan atau suplemen.
                  </p>
                  <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                    Klasifikasi Vitamin:
                  </h3>
                  <h4 style={{ fontSize: "16px", marginTop: "15px" }}>
                    <strong>1. Vitamin Larut Lemak:</strong>
                  </h4>
                  <div
                    className="alert alert-info"
                    style={{ lineHeight: "1.8", backgroundColor: "#d1ecf1" }}
                  >
                    <ul style={{ marginTop: "10px" }}>
                      <li>
                        <strong>Vitamin A:</strong> Disimpan di hati, penting untuk penglihatan dan kekebalan tubuh
                      </li>
                      <li>
                        <strong>Vitamin D:</strong> Diproduksi tubuh saat terkena sinar matahari, penting untuk tulang
                      </li>
                      <li>
                        <strong>Vitamin E:</strong> Antioksidan kuat, melindungi sel
                      </li>
                      <li>
                        <strong>Vitamin K:</strong> Penting untuk pembekuan darah dan kesehatan tulang
                      </li>
                    </ul>
                    <br />
                    <strong>Catatan:</strong> Vitamin ini disimpan dalam tubuh, sehingga kelebihan dapat menyebabkan masalah kesehatan.
                  </div>
                  <h4 style={{ fontSize: "16px", marginTop: "15px" }}>
                    <strong>2. Vitamin Larut Air:</strong>
                  </h4>
                  <div
                    className="alert alert-success"
                    style={{ lineHeight: "1.8", backgroundColor: "#d4edda" }}
                  >
                    <ul style={{ marginTop: "10px" }}>
                      <li>
                        <strong>Vitamin C:</strong> Antioksidan, memperkuat kekebalan tubuh, membantu penyerapan zat besi
                      </li>
                      <li>
                        <strong>Vitamin B kompleks (8 jenis):</strong> Penting untuk produksi energi dan saraf
                      </li>
                    </ul>
                    <br />
                    <strong>Catatan:</strong> Tidak disimpan dalam tubuh, sehingga harus dikonsumsi setiap hari.
                  </div>
                  <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                    Pentingnya Vitamin:
                  </h3>
                  <ol style={{ lineHeight: "1.8" }}>
                    <li>
                      <strong>Produksi Energi:</strong> Vitamin B membantu mengubah makanan menjadi energi
                    </li>
                    <li>
                      <strong>Memperkuat Kekebalan Tubuh:</strong> Vitamin C, D, A melindungi dari penyakit
                    </li>
                    <li>
                      <strong>Kesehatan Tulang:</strong> Vitamin D, K, C penting untuk tulang yang kuat
                    </li>
                    <li>
                      <strong>Kesehatan Kulit:</strong> Vitamin A, C, E menjaga kulit tetap sehat
                    </li>
                    <li>
                      <strong>Pencegahan Penyakit Kronis:</strong> Antioksidan melindungi dari kanker dan penyakit jantung
                    </li>
                  </ol>
                  <div
                    className="alert alert-warning mt-3"
                    style={{ lineHeight: "1.8", backgroundColor: "#fff3cd" }}
                  >
                    <strong>⚠️ Peringatan:</strong> Meskipun penting, konsumsi vitamin berlebihan dapat berbahaya. Patuhi dosis yang direkomendasikan dan konsultasikan dengan dokter sebelum mengonsumsi suplemen apa pun.
                  </div>
                </div>
              </div>
              <div className="info-section">
                <div className="info-section-header">
                  <strong>Sumber Vitamin Alami</strong>
                </div>
                <div className="info-section-body">
                  <p style={{ lineHeight: "1.8" }}>
                    Cara terbaik untuk mendapatkan vitamin adalah melalui <strong>pola makan seimbang dan bervariasi</strong>. Berikut sumber alami utama setiap vitamin:
                  </p>
                  <div
                    style={{
                      lineHeight: "1.8",
                      marginTop: "15px",
                      padding: "15px",
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                    }}
                  >
                    <h4 style={{ fontSize: "16px", color: "#198754" }}>
                      <strong>Vitamin A:</strong>
                    </h4>
                    <ul>
                      <li>Wortel, ubi jalar, labu</li>
                      <li>Bayam, kangkung, brokoli</li>
                      <li>Hati, telur, susu</li>
                      <li>Mangga, aprikot, melon</li>
                    </ul>
                  </div>
                  <div
                    style={{
                      lineHeight: "1.8",
                      marginTop: "15px",
                      padding: "15px",
                      backgroundColor: "#fff3cd",
                      borderRadius: "8px",
                    }}
                  >
                    <h4 style={{ fontSize: "16px", color: "#856404" }}>
                      <strong>Vitamin C:</strong>
                    </h4>
                    <ul>
                      <li>Jeruk (jeruk, lemon, jeruk bali)</li>
                      <li>Paprika (warna-warni)</li>
                      <li>Stroberi, kiwi, jambu biji</li>
                      <li>Brokoli, tomat</li>
                    </ul>
                  </div>
                  <div
                    style={{
                      lineHeight: "1.8",
                      marginTop: "15px",
                      padding: "15px",
                      backgroundColor: "#d1ecf1",
                      borderRadius: "8px",
                    }}
                  >
                    <h4 style={{ fontSize: "16px", color: "#0c5460" }}>
                      <strong>Vitamin D:</strong>
                    </h4>
                    <ul>
                      <li>
                        <strong>Sinar Matahari:</strong> 10-30 menit setiap hari (sumber terpenting)
                      </li>
                      <li>Ikan berlemak (salmon, tuna, sarden)</li>
                      <li>Kuning telur</li>
                      <li>Susu dan makanan yang diperkaya vitamin D</li>
                    </ul>
                  </div>
                  <div
                    style={{
                      lineHeight: "1.8",
                      marginTop: "15px",
                      padding: "15px",
                      backgroundColor: "#d4edda",
                      borderRadius: "8px",
                    }}
                  >
                    <h4 style={{ fontSize: "16px", color: "#155724" }}>
                      <strong>Vitamin E:</strong>
                    </h4>
                    <ul>
                      <li>Kacang-kacangan (almond, hazelnut, kacang tanah)</li>
                      <li>Biji-bijian (bunga matahari, labu)</li>
                      <li>Minyak nabati (minyak zaitun, minyak bunga matahari)</li>
                      <li>Bayam, brokoli, alpukat</li>
                    </ul>
                  </div>
                  <div
                    style={{
                      lineHeight: "1.8",
                      marginTop: "15px",
                      padding: "15px",
                      backgroundColor: "#f8d7da",
                      borderRadius: "8px",
                    }}
                  >
                    <h4 style={{ fontSize: "16px", color: "#721c24" }}>
                      <strong>Vitamin B Kompleks:</strong>
                    </h4>
                    <ul>
                      <li>
                        <strong>B1, B2, B3:</strong> Biji-bijian utuh, daging, kacang-kacangan
                      </li>
                      <li>
                        <strong>B6:</strong> Ayam, ikan, kentang, pisang
                      </li>
                      <li>
                        <strong>B9 (Asam Folat):</strong> Sayuran berdaun hijau, jeruk, kacang-kacangan
                      </li>
                      <li>
                        <strong>B12:</strong> Daging, ikan, telur, produk susu
                      </li>
                    </ul>
                  </div>
                  <div
                    className="alert alert-info mt-3"
                    style={{ lineHeight: "1.8" }}
                  >
                    <strong>💡 Tips Emas:</strong> Konsumsi "pelangi makanan" - yaitu mengonsumsi makanan dengan berbagai warna setiap hari memastikan Anda mendapatkan berbagai macam vitamin dan mineral.
                  </div>
                </div>
              </div>
              <div className="info-section">
                <div className="info-section-header">
                  <strong>Gejala Kekurangan Vitamin dan Pengobatannya</strong>
                </div>
                <div className="info-section-body">
                  <p style={{ lineHeight: "1.8" }}>
                    <strong>Kekurangan vitamin</strong> dapat menyebabkan berbagai masalah kesehatan. Berikut gejala umum dan cara pengobatannya:
                  </p>
                  <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                    Gejala Kekurangan Vitamin yang Umum:
                  </h3>
                  <div
                    className="alert alert-warning"
                    style={{ lineHeight: "1.8", backgroundColor: "#fff3cd" }}
                  >
                    <strong>Kekurangan Vitamin D:</strong>
                    <ul style={{ marginTop: "10px" }}>
                      <li>Nyeri dan kelemahan tulang dan otot</li>
                      <li>Kelelahan dan kelemahan terus-menerus</li>
                      <li>Sistem kekebalan lemah dan sering terkena infeksi</li>
                      <li>Perubahan suasana hati dan depresi</li>
                      <li>Penyembuhan luka lambat</li>
                    </ul>
                    <br />
                    <strong>Pengobatan:</strong> Paparan sinar matahari, konsumsi ikan berlemak, suplemen vitamin D
                  </div>
                  <div
                    className="alert alert-danger"
                    style={{ lineHeight: "1.8", backgroundColor: "#f8d7da" }}
                  >
                    <strong>Kekurangan Vitamin B12:</strong>
                    <ul style={{ marginTop: "10px" }}>
                      <li>Anemia (pucat, kelelahan, sesak napas)</li>
                      <li>Kesemutan dan mati rasa di tangan dan kaki</li>
                      <li>Kesulitan keseimbangan dan berjalan</li>
                      <li>Masalah memori dan konsentrasi</li>
                      <li>Radang dan kemerahan lidah</li>
                    </ul>
                    <br />
                    <strong>Pengobatan:</strong> Daging, ikan, telur, suplemen B12 (terutama untuk vegetarian)
                  </div>
                  <div
                    className="alert alert-info"
                    style={{ lineHeight: "1.8", backgroundColor: "#d1ecf1" }}
                  >
                    <strong>Kekurangan Vitamin C:</strong>
                    <ul style={{ marginTop: "10px" }}>
                      <li>Gusi berdarah dan bengkak</li>
                      <li>Penyembuhan luka lambat</li>
                      <li>Kulit kering dan mengelupas</li>
                      <li>Sistem kekebalan lemah</li>
                      <li>Nyeri sendi</li>
                    </ul>
                    <br />
                    <strong>Pengobatan:</strong> Jeruk, paprika, stroberi, brokoli
                  </div>
                  <div
                    className="alert alert-success"
                    style={{ lineHeight: "1.8", backgroundColor: "#d4edda" }}
                  >
                    <strong>Kekurangan Asam Folat (B9):</strong>
                    <ul style={{ marginTop: "10px" }}>
                      <li>Anemia makrositik</li>
                      <li>Kelelahan dan kelemahan</li>
                      <li>Sariawan dan lidah</li>
                      <li>Uban dini</li>
                      <li>
                        <strong>Sangat berbahaya bagi ibu hamil:</strong> Cacat janin
                      </li>
                    </ul>
                    <br />
                    <strong>Pengobatan:</strong> Sayuran berdaun hijau, kacang-kacangan, jeruk, suplemen asam folat
                  </div>
                  <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                    Kelompok yang Paling Rentan Kekurangan Vitamin:
                  </h3>
                  <ul style={{ lineHeight: "1.8" }}>
                    <li>
                      <strong>Vegetarian:</strong> Rentan kekurangan B12, D, zat besi, seng
                    </li>
                    <li>
                      <strong>Lansia:</strong> Rentan kekurangan D, B12, kalsium
                    </li>
                    <li>
                      <strong>Ibu Hamil dan Menyusui:</strong> Membutuhkan dosis lebih tinggi dari asam folat dan zat besi
                    </li>
                    <li>
                      <strong>Orang yang Tidak Terpapar Sinar Matahari:</strong> Rentan kekurangan vitamin D
                    </li>
                    <li>
                      <strong>Penderita Gangguan Usus:</strong> Rentan kekurangan semua vitamin karena malabsorpsi
                    </li>
                  </ul>
                  <div
                    className="alert alert-danger mt-3"
                    style={{ lineHeight: "1.8" }}
                  >
                    <strong>⚠️ Kapan Harus ke Dokter?</strong>
                    <ul style={{ marginTop: "10px" }}>
                      <li>Jika Anda mengalami gejala kekurangan vitamin yang terus-menerus</li>
                      <li>Sebelum mengonsumsi suplemen apa pun</li>
                      <li>Jika Anda sedang hamil atau berencana hamil</li>
                      <li>Jika Anda menjalani pola makan vegetarian yang ketat</li>
                      <li>Jika Anda memiliki masalah penyerapan nutrisi</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <br />
            {/* FAQ Section */}
            <h2 style={{ fontSize: "20px", marginBottom: "15px" }}>
              Pertanyaan Umum tentang Vitamin
            </h2>
            <div className="info-sections-container">
              <div className="info-section">
                <div className="info-section-header">
                  <h3 style={{ fontSize: "16px" }}>
                    Apakah saya perlu mengonsumsi suplemen vitamin?
                  </h3>
                </div>
                <div className="info-section-body">
                  <p style={{ lineHeight: "1.8" }}>
                    Dalam kebanyakan kasus, semua vitamin dapat diperoleh dari <strong>pola makan seimbang dan bervariasi</strong>. Namun ada kondisi yang mungkin memerlukan suplemen:
                  </p>
                  <h4 style={{ fontSize: "16px", marginTop: "15px" }}>
                    <strong>Kapan Suplemen Diperlukan:</strong>
                  </h4>
                  <div
                    className="alert alert-success"
                    style={{ lineHeight: "1.8", backgroundColor: "#d4edda" }}
                  >
                    <ul style={{ marginTop: "10px" }}>
                      <li>
                        <strong>Ibu Hamil dan Menyusui:</strong> Asam folat (400-800 mikrogram) dan zat besi
                      </li>
                      <li>
                        <strong>Vegetarian:</strong> Vitamin B12 (tidak ditemukan pada tumbuhan)
                      </li>
                      <li>
                        <strong>Lansia di atas 50 tahun:</strong> Vitamin D, B12, kalsium
                      </li>
                      <li>
                        <strong>Mereka yang Tidak Terpapar Sinar Matahari:</strong> Vitamin D
                      </li>
                      <li>
                        <strong>Setelah Operasi Bariatrik:</strong> Sebagian besar vitamin karena malabsorpsi
                      </li>
                    </ul>
                  </div>
                  <h4 style={{ fontSize: "16px", marginTop: "15px" }}>
                    <strong>Kapan Anda Tidak Perlu Suplemen:</strong>
                  </h4>
                  <ul style={{ lineHeight: "1.8" }}>
                    <li>Jika Anda menjalani pola makan seimbang</li>
                    <li>Jika Anda rutin terpapar sinar matahari</li>
                    <li>Jika Anda tidak memiliki masalah penyerapan</li>
                    <li>Jika Anda tidak menunjukkan gejala kekurangan</li>
                  </ul>
                  <div
                    className="alert alert-warning mt-3"
                    style={{ lineHeight: "1.8" }}
                  >
                    <strong>⚠️ Peringatan Penting:</strong> Konsumsi vitamin berlebihan (terutama A, D, E, K) dapat menjadi racun. Konsultasikan dengan dokter Anda selalu sebelum mengonsumsi suplemen apa pun.
                  </div>
                </div>
              </div>
              <div className="info-section">
                <div className="info-section-header">
                  <h3 style={{ fontSize: "16px" }}>
                    Apa perbedaan antara vitamin alami dan sintetis?
                  </h3>
                </div>
                <div className="info-section-body">
                  <p style={{ lineHeight: "1.8" }}>
                    Ada perbedaan antara vitamin dari <strong>sumber alami</strong> dan <strong>suplemen sintetis</strong>:
                  </p>
                  <h4 style={{ fontSize: "16px", marginTop: "15px" }}>
                    <strong>Vitamin Alami (dari makanan):</strong>
                  </h4>
                  <div
                    className="alert alert-success"
                    style={{ lineHeight: "1.8", backgroundColor: "#d4edda" }}
                  >
                    <strong>Kelebihan:</strong>
                    <ul style={{ marginTop: "10px" }}>
                      <li>Hadir bersama nutrisi lain yang bermanfaat (serat, mineral, antioksidan)</li>
                      <li>Penyerapan lebih baik oleh tubuh</li>
                      <li>Risiko overdosis lebih rendah</li>
                      <li>Mengandung senyawa nabati yang bermanfaat</li>
                    </ul>
                  </div>
                  <h4 style={{ fontSize: "16px", marginTop: "15px" }}>
                    <strong>Suplemen Sintetis:</strong>
                  </h4>
                  <div
                    className="alert alert-info"
                    style={{ lineHeight: "1.8", backgroundColor: "#d1ecf1" }}
                  >
                    <strong>Kelebihan:</strong>
                    <ul style={{ marginTop: "10px" }}>
                      <li>Mudah digunakan dan dosis terstandarisasi</li>
                      <li>Berguna dalam kasus kekurangan berat</li>
                      <li>Diperlukan untuk beberapa kelompok (ibu hamil, vegetarian)</li>
                    </ul>
                    <br />
                    <strong>Kekurangan:</strong>
                    <ul>
                      <li>Mungkin tidak diserap sebaik sumber alami</li>
                      <li>Risiko overdosis</li>
                      <li>Tidak mengandung nutrisi tambahan</li>
                      <li>Dapat berinteraksi dengan beberapa obat</li>
                    </ul>
                  </div>
                  <div
                    className="alert alert-success mt-3"
                    style={{ lineHeight: "1.8" }}
                  >
                    <strong>✓ Tips Emas:</strong> Prioritas utama selalu mendapatkan vitamin dari makanan. Suplemen hanya membantu saat diperlukan, dan bukan pengganti nutrisi sehat.
                  </div>
                </div>
              </div>
              <div className="info-section">
                <div className="info-section-header">
                  <h3 style={{ fontSize: "16px" }}>
                    Bisakah semua vitamin dikonsumsi bersama?
                  </h3>
                </div>
                <div className="info-section-body">
                  <p style={{ lineHeight: "1.8" }}>
                    Beberapa vitamin dapat <strong>berinteraksi</strong> satu sama lain, mempengaruhi penyerapannya:
                  </p>
                  <h4 style={{ fontSize: "16px", marginTop: "15px" }}>
                    <strong>Interaksi Positif (dikonsumsi bersama):</strong>
                  </h4>
                  <div
                    className="alert alert-success"
                    style={{ lineHeight: "1.8", backgroundColor: "#d4edda" }}
                  >
                    <ul style={{ marginTop: "10px" }}>
                      <li>
                        <strong>Vitamin D + Kalsium:</strong> Meningkatkan penyerapan kalsium
                      </li>
                      <li>
                        <strong>Vitamin C + Zat Besi:</strong> Meningkatkan penyerapan zat besi
                      </li>
                      <li>
                        <strong>Vitamin K + Vitamin D:</strong> Bekerja sama untuk kesehatan tulang
                      </li>
                      <li>
                        <strong>Vitamin B Kompleks:</strong> Bekerja bersama secara sinergis
                      </li>
                    </ul>
                  </div>
                  <h4 style={{ fontSize: "16px", marginTop: "15px" }}>
                    <strong>Interaksi Negatif (jangan dikonsumsi bersama):</strong>
                  </h4>
                  <div
                    className="alert alert-warning"
                    style={{ lineHeight: "1.8", backgroundColor: "#fff3cd" }}
                  >
                    <ul style={{ marginTop: "10px" }}>
                      <li>
                        <strong>Kalsium + Zat Besi:</strong> Bersaing untuk penyerapan (jarak 2-3 jam)
                      </li>
                      <li>
                        <strong>Kalsium + Seng:</strong> Kalsium mengurangi penyerapan seng
                      </li>
                      <li>
                        <strong>Vitamin E + Vitamin K:</strong> Dosis tinggi E dapat mengganggu K
                      </li>
                    </ul>
                  </div>
                  <h4 style={{ fontSize: "16px", marginTop: "15px" }}>
                    <strong>Waktu Terbaik Mengonsumsi Vitamin:</strong>
                  </h4>
                  <ul style={{ lineHeight: "1.8" }}>
                    <li>
                      <strong>Pagi saat sarapan:</strong> Vitamin B kompleks (untuk energi)
                    </li>
                    <li>
                      <strong>Bersama makanan:</strong> Vitamin larut lemak (A, D, E, K)
                    </li>
                    <li>
                      <strong>Sebelum tidur:</strong> Kalsium dan magnesium (membantu relaksasi)
                    </li>
                    <li>
                      <strong>Saat perut kosong:</strong> Zat besi (untuk penyerapan terbaik)
                    </li>
                  </ul>
                  <div
                    className="alert alert-info mt-3"
                    style={{ lineHeight: "1.8" }}
                  >
                    <strong>💡 Tips:</strong> Jika Anda mengonsumsi beberapa suplemen, konsultasikan dengan apoteker atau dokter untuk mengetahui cara terbaik mendistribusikannya sepanjang hari.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <br />
      </div>
    </div>
  );
};
export default VitaminDailyDoseCalculator;